/**
 * Sound cues for the player. A small set of inlined clips, mapped to reveal
 * events (an element appearing, an annotation, a spotlight, a stroke). Hosts
 * call `playSfx(cue)` when something is newly revealed; rapid bursts are
 * throttled/deduped so word-timed reveals don't turn into a machine-gun, and the
 * volume sits low under the narration. Disable via `setSfxEnabled(false)`.
 */

import { click, fast_swoosh, snap, swoosh, woosh } from './sfx-data';

export type SfxCue = 'click' | 'snap' | 'swoosh' | 'woosh' | 'fast_swoosh';

const SOURCES: Record<SfxCue, string> = { click, snap, swoosh, woosh, fast_swoosh };

let enabled = false; // muted (user 2026-08-13: no click/swoosh for now) — setSfxEnabled(true) re-enables
let volume = 0.3;
let lastAny = 0;
const lastByCue: Record<string, number> = {};

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export function setSfxEnabled(v: boolean): void {
  enabled = v;
}
export function setSfxVolume(v: number): void {
  volume = Math.max(0, Math.min(1, v));
}

/** Play a cue, with a global min-gap (anti machine-gun) + per-cue dedupe. */
export function playSfx(cue: SfxCue): void {
  if (!enabled || typeof Audio === 'undefined') return;
  const t = now();
  if (t - lastAny < 70) return; // global gap — collapse simultaneous reveals
  if (t - (lastByCue[cue] ?? -1e9) < 140) return; // same-cue dedupe
  lastAny = t;
  lastByCue[cue] = t;
  try {
    const a = new Audio(SOURCES[cue]);
    a.volume = volume;
    void a.play().catch(() => {});
  } catch {
    /* ignore */
  }
}

/** Cue for an element reveal, by element type. */
export function cueForElement(type: string): SfxCue {
  switch (type) {
    case 'callout':
      // Pairs with the hanging-sign swing-in (`callout-swing`).
      return 'swoosh';
    case 'image':
    case 'mcq':
    case 'chip':
    case 'icon':
    case 'mermaid':
      return 'snap';
    case '3d':
    case 'video':
    case 'embed':
      return 'woosh';
    default: // text, latex, shape, …
      return 'click';
  }
}

/** Cue for non-element reveal events. */
export const CUE_ANNOTATION: SfxCue = 'click';
export const CUE_SPOTLIGHT: SfxCue = 'swoosh';
export const CUE_CAMERA: SfxCue = 'fast_swoosh';
export const CUE_STROKE: SfxCue = 'fast_swoosh';
